import React, { useEffect, useMemo, useState } from "react";
import { CircularProgress, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import {
    CartesianGrid,
    Line,
    LineChart,
    Tooltip as RechartsTooltip,
    XAxis,
    YAxis,
} from "recharts";
import { publicApi } from "../../api/http";
import { useChartWidth } from "./useChartWidth";

import "./PlayerArrivalPanel.css";

const STAGE_ORDER = ["M0", "M1", "M2", "M3", "M4", "M5"];
const STAGE_COLORS = {
    M0: "#ef767a",
    M1: "#49beaa",
    M2: "#6b9bd1",
    M3: "#e0a458",
    M4: "#c084fc",
    M5: "#94a3b8",
};
const HORIZONS = ["dy0", "dy1", "dy2", "dy3"];
const AXIS_COLOR = "#a1a8b3";
const GRID_COLOR = "rgba(255,255,255,0.08)";
const DEFAULT_CHART_HEIGHT = 200;

function parseRows(data) {
    if (typeof data === "string") {
        try {
            return JSON.parse(data);
        } catch {
            return [];
        }
    }
    return Array.isArray(data) ? data : [];
}

function isArrivalKind(kind) {
    return /^M[0-5]$/.test(kind || "");
}

function stageIndex(kind) {
    const match = /^M([0-5])$/.exec(kind || "");
    return match ? Number(match[1]) : null;
}

function seasonLabel(year) {
    if (year == null || Number.isNaN(Number(year))) return "";
    const start = Number(year);
    return `${start}–${String(start + 1).slice(-2)}`;
}

function formatPct(value) {
    if (value == null || Number.isNaN(Number(value))) return "—";
    return `${(Number(value) * 100).toFixed(0)}%`;
}

function formatRank(value) {
    if (value == null || Number.isNaN(Number(value))) return "—";
    return `#${Number(value)}`;
}

function pickLatestRows(rows) {
    const arrival = rows.filter((row) => isArrivalKind(row.kind));
    if (!arrival.length) return [];
    const latestDate = arrival
        .map((row) => row.model_snapshot_date || "")
        .sort()
        .at(-1);
    const dated = arrival.filter((row) => (row.model_snapshot_date || "") === latestDate);
    const versions = [...new Set(dated.map((row) => row.model_version).filter(Boolean))];
    const latestVersion = versions.sort().at(-1);
    return dated.filter((row) => !latestVersion || row.model_version === latestVersion);
}

function ChartTooltip({ active, payload, label, metric }) {
    if (!active || !payload?.length) return null;
    const formatValue = metric === "rank"
        ? (value) => formatRank(value)
        : (value) => formatPct(value / 100);
    return (
        <div className="player-arrival-tooltip">
            <div className="player-arrival-tooltip-title">{label}</div>
            {payload.map((entry) => (
                <div key={entry.dataKey} className="player-arrival-tooltip-row">
                    <span
                        className="player-arrival-tooltip-dot"
                        style={{ backgroundColor: entry.color }}
                    />
                    <span>{entry.name}</span>
                    <strong>{formatValue(entry.value)}</strong>
                </div>
            ))}
        </div>
    );
}

function MetricCard({ label, value, hint }) {
    return (
        <div className="player-arrival-metric">
            <span className="player-arrival-metric-label">{label}</span>
            <span className="player-arrival-metric-value">{value}</span>
            {hint ? <span className="player-arrival-metric-hint">{hint}</span> : null}
        </div>
    );
}

function PlayerArrivalPanel({
    playerId,
    onHasData,
    initialMetric = "probability",
    initialStages = [],
    onShareStateChange,
}) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [selectedStages, setSelectedStages] = useState([]);
    const [metric, setMetric] = useState(initialMetric === "rank" ? "rank" : "probability");
    const [chartRef, chartWidth, chartHeight] = useChartWidth(640, DEFAULT_CHART_HEIGHT);

    useEffect(() => {
        if (!playerId) return undefined;
        let cancelled = false;
        setLoading(true);
        setError(null);

        publicApi
            .get(`/prospect_model_projections/${playerId}`)
            .then((response) => {
                if (cancelled) return;
                const latest = pickLatestRows(parseRows(response.data));
                setRows(latest);
                const kinds = STAGE_ORDER.filter((kind) => latest.some((row) => row.kind === kind));
                const requested = (initialStages || []).filter((kind) => kinds.includes(kind));
                setSelectedStages(requested.length ? requested : kinds);
                onHasData?.(latest.length > 0);
            })
            .catch((err) => {
                if (cancelled) return;
                console.error("Error fetching arrival projections:", err);
                setError("Failed to load NHL arrival projections.");
                setRows([]);
                onHasData?.(false);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [playerId]);

    useEffect(() => {
        onShareStateChange?.({
            metric,
            selectedStages,
        });
    }, [metric, selectedStages, onShareStateChange]);

    const stages = useMemo(
        () => STAGE_ORDER.filter((kind) => rows.some((row) => row.kind === kind)),
        [rows]
    );

    const currentRow = useMemo(
        () => rows.find((row) => row.is_dy0_stage) || rows[rows.length - 1] || null,
        [rows]
    );

    const probabilityChartData = useMemo(() => {
        const bySeason = new Map();
        rows.forEach((row) => {
            const stage = stageIndex(row.kind);
            const draftYear = Number(row.draft_year);
            if (stage == null || Number.isNaN(draftYear)) return;
            HORIZONS.forEach((key, horizon) => {
                const year = draftYear + stage + horizon;
                const label = seasonLabel(year);
                if (!bySeason.has(label)) {
                    bySeason.set(label, { season: label, sortKey: year });
                }
                const prob = row[key];
                if (prob != null && !Number.isNaN(Number(prob))) {
                    bySeason.get(label)[row.kind] = Number(prob) * 100;
                }
            });
        });
        return [...bySeason.values()].sort((a, b) => a.sortKey - b.sortKey);
    }, [rows]);

    const rankChartData = useMemo(() => {
        return STAGE_ORDER.filter((kind) => selectedStages.includes(kind)).map((kind) => {
            const row = rows.find((item) => item.kind === kind);
            return {
                season: kind,
                rank: row?.blend_rank == null || Number.isNaN(Number(row.blend_rank))
                    ? null
                    : Number(row.blend_rank),
            };
        });
    }, [rows, selectedStages]);

    const chartData = metric === "rank" ? rankChartData : probabilityChartData;

    const handleStageToggle = (_event, next) => {
        if (!next?.length) return;
        setSelectedStages(next);
    };

    const handleMetricToggle = (_event, next) => {
        if (!next) return;
        setMetric(next);
    };

    if (loading) {
        return (
            <div className="player-nhl-stats-loading">
                <CircularProgress size={28} />
            </div>
        );
    }

    if (error) {
        return (
            <Typography variant="body1" component="div" className="empty-tab-message">
                {error}
            </Typography>
        );
    }

    if (!rows.length || !currentRow) {
        return null;
    }

    const currentKind = currentRow.kind;
    const thisSeason = seasonLabel(Number(currentRow.draft_year) + stageIndex(currentKind));

    return (
        <div className="player-arrival-panel">
            <div className="player-arrival-toolbar">
                {stages.length > 1 ? (
                    <ToggleButtonGroup
                        value={selectedStages}
                        onChange={handleStageToggle}
                        size="small"
                        className="player-arrival-stage-toggle"
                        aria-label="Draft-stage models"
                    >
                        {stages.map((kind) => (
                            <ToggleButton key={kind} value={kind}>
                                {kind}
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                ) : (
                    <span className="player-arrival-toolbar-label">{currentKind} outlook</span>
                )}
                <ToggleButtonGroup
                    value={metric}
                    exclusive
                    onChange={handleMetricToggle}
                    size="small"
                    className="player-arrival-metric-toggle"
                    aria-label="Arrival chart metric"
                >
                    <ToggleButton value="probability">Probability</ToggleButton>
                    <ToggleButton value="rank">Class rank</ToggleButton>
                </ToggleButtonGroup>
                <Link to="/how-projections-work" className="player-arrival-method-link">
                    How this works
                </Link>
            </div>

            <div className="player-arrival-metrics">
                <MetricCard
                    label={`${thisSeason} (now)`}
                    value={formatPct(currentRow.dy0)}
                    hint={`${currentKind} · this season`}
                />
                <MetricCard
                    label="Next season"
                    value={formatPct(currentRow.dy1)}
                    hint={seasonLabel(Number(currentRow.draft_year) + stageIndex(currentKind) + 1)}
                />
                <MetricCard
                    label={`${currentKind} class rank`}
                    value={formatRank(currentRow.blend_rank)}
                    hint="this draft class · current stage"
                />
            </div>

            <div className="player-arrival-chart" ref={chartRef}>
                <LineChart
                    width={chartWidth}
                    height={chartHeight}
                    data={chartData}
                    margin={{ top: 6, right: 8, left: 0, bottom: 16 }}
                >
                    <CartesianGrid strokeDasharray="3 3" stroke={GRID_COLOR} />
                    <XAxis
                        dataKey="season"
                        stroke={AXIS_COLOR}
                        tick={{ fill: AXIS_COLOR, fontSize: 12 }}
                        tickLine={{ stroke: AXIS_COLOR }}
                        axisLine={{ stroke: AXIS_COLOR }}
                    />
                    <YAxis
                        stroke={AXIS_COLOR}
                        tick={{ fill: AXIS_COLOR, fontSize: 12 }}
                        tickLine={{ stroke: AXIS_COLOR }}
                        axisLine={{ stroke: AXIS_COLOR }}
                        tickFormatter={(value) => (metric === "rank" ? `#${value}` : `${value}%`)}
                        domain={metric === "rank" ? [1, "auto"] : [0, 100]}
                        reversed={metric === "rank"}
                        allowDecimals={false}
                        width={42}
                    />
                    <RechartsTooltip
                        content={<ChartTooltip metric={metric} />}
                        cursor={{ stroke: "rgba(148, 192, 115, 0.35)" }}
                        wrapperStyle={{ outline: "none" }}
                    />
                    {metric === "rank" ? (
                        <Line
                            type="monotone"
                            dataKey="rank"
                            name="Class rank"
                            stroke={STAGE_COLORS[currentKind] || "#49beaa"}
                            strokeWidth={3}
                            dot={{ r: 4 }}
                            connectNulls={false}
                        />
                    ) : (
                        selectedStages.map((kind) => (
                            <Line
                                key={kind}
                                type="monotone"
                                dataKey={kind}
                                name={kind}
                                stroke={STAGE_COLORS[kind]}
                                strokeWidth={kind === currentKind ? 3 : 2}
                                dot={{ r: kind === currentKind ? 4 : 3 }}
                                connectNulls={false}
                            />
                        ))
                    )}
                </LineChart>
            </div>
        </div>
    );
}

export default PlayerArrivalPanel;
